import { useMemo } from "react";
import * as THREE from "three";

/** Vertical gradient as a CanvasTexture (top->bottom). Used for sky & sea so the
 *  backdrop reads as graded light instead of hard flat colour bands. */
function makeVGradient(stops: Array<[number, string]>): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = 4;
  c.height = 256;
  const ctx = c.getContext("2d")!;
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  for (const [o, col] of stops) g.addColorStop(o, col);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 4, 256);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** Soft radial glow (sun / specular bloom stand-in) as an additive sprite map. */
function makeRadialGlow(inner: string, outer: string): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, inner);
  g.addColorStop(1, outer);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** A graded sea with wavy crest lines so the water reads as moving swell instead
 *  of a flat blue band. Crests bunch toward the top (horizon) for a sense of
 *  perspective and get softer with distance. */
function makeSeaTexture(): THREE.CanvasTexture {
  const w = 512;
  const h = 256;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d")!;
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, "#cdeef4");
  g.addColorStop(0.1, "#7fcfe0");
  g.addColorStop(0.45, "#2ea6c6");
  g.addColorStop(1, "#125f78");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  for (let i = 0; i < 52; i++) {
    const t = i / 52;
    const y = Math.pow(t, 1.5) * h; // perspective: crests crowd the horizon (top)
    const amp = 1.5 + t * 8;
    const alpha = 0.07 + (1 - t) * 0.2;
    ctx.strokeStyle = `rgba(238,251,255,${alpha})`;
    ctx.lineWidth = 0.6 + t * 1.8;
    ctx.beginPath();
    for (let x = 0; x <= w; x += 8) {
      const yy =
        y + Math.sin(x * 0.03 + i * 1.7) * amp + Math.sin(x * 0.011 + i) * amp * 0.45;
      if (x === 0) ctx.moveTo(x, yy);
      else ctx.lineTo(x, yy);
    }
    ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/** Scalloped white surf band (transparent top) for the shoreline foam line. */
function makeFoamTexture(): THREE.CanvasTexture {
  const w = 512;
  const h = 64;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d")!;
  ctx.clearRect(0, 0, w, h);
  for (let pass = 0; pass < 3; pass++) {
    ctx.fillStyle = `rgba(255,255,255,${0.55 - pass * 0.14})`;
    ctx.beginPath();
    ctx.moveTo(0, h);
    for (let x = 0; x <= w; x += 6) {
      const edge =
        h * 0.5 + Math.sin(x * 0.05 + pass * 2) * 8 + Math.sin(x * 0.13 + pass) * 5 - pass * 5;
      ctx.lineTo(x, edge);
    }
    ctx.lineTo(w, h);
    ctx.closePath();
    ctx.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * Procedural beach hotel-suite (zero external assets), used by the deepfake
 * whaling scenario. Same coordinate frame as <Office/> so the scenario camera
 * waypoints line up:
 *   - desk centred at the origin, learner looks toward -z
 *   - laptop/monitor device anchor at ~[0, 0.95, -0.35]
 *   - the wall behind the desk (-z) is an open balcony with an ocean view
 *
 * Reads as a sunny suite: light sandy floor, white walls, a glass balcony rail,
 * sky + sea backdrop, a palm, and a warm key light layered on top of the
 * scene's lights.
 */
export function HotelSuite() {
  return (
    <group>
      {/* Gentle warm fill on top of the Scene's IBL + sun (kept low to avoid
          washing out the image-based lighting). */}
      <ambientLight intensity={0.22} color="#fff3df" />
      <directionalLight position={[-3.5, 4.5, 1.5]} intensity={0.5} color="#fff1d4" />

      <OceanBackdrop />

      {/* floor — light sandy tile */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[18, 18]} />
        <meshStandardMaterial color="#e9dcc4" roughness={0.9} />
      </mesh>
      {/* soft rug under the desk */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0.25]} receiveShadow>
        <planeGeometry args={[4.4, 3.2]} />
        <meshStandardMaterial color="#dccba8" roughness={1} />
      </mesh>

      {/* left wall (white plaster) with a framed print */}
      <mesh position={[-5, 2.1, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <boxGeometry args={[10, 4.2, 0.2]} />
        <meshStandardMaterial color="#f4efe6" roughness={0.96} />
      </mesh>
      <group position={[-4.88, 2.0, -0.4]} rotation={[0, Math.PI / 2, 0]}>
        <mesh>
          <boxGeometry args={[1.4, 1.0, 0.04]} />
          <meshStandardMaterial color="#cbb489" roughness={0.6} />
        </mesh>
        <mesh position={[0, 0, 0.03]}>
          <planeGeometry args={[1.24, 0.84]} />
          <meshStandardMaterial color="#bfe3ea" roughness={0.8} />
        </mesh>
        <mesh position={[0, -0.18, 0.032]}>
          <planeGeometry args={[1.24, 0.34]} />
          <meshStandardMaterial color="#e7d3a6" roughness={0.85} />
        </mesh>
      </group>

      {/* right wall (short return, white) */}
      <mesh position={[3.6, 2.1, -1.6]} receiveShadow>
        <boxGeometry args={[3.0, 4.2, 0.2]} />
        <meshStandardMaterial color="#efe9df" roughness={0.96} />
      </mesh>

      <BalconyWall />
      <Desk />
      <Laptop />
      <LoungeChair />
      <PalmPlant position={[-2.3, 0, -0.6]} scale={0.9} />
      <PalmPlant position={[3.4, 0, -0.8]} scale={0.7} />
      <Bed />
    </group>
  );
}

/** Sky + sea + sand seen through the open balcony (large planes well behind -z). */
function OceanBackdrop() {
  const skyTex = useMemo(
    () =>
      makeVGradient([
        [0, "#3f9fd6"],
        [0.55, "#86c8ea"],
        [0.86, "#cfeaf6"],
        [1, "#eaf7fb"],
      ]),
    []
  );
  const seaTex = useMemo(() => makeSeaTexture(), []);
  const foamTex = useMemo(() => makeFoamTexture(), []);
  const sunTex = useMemo(() => makeRadialGlow("rgba(255,250,235,0.95)", "rgba(255,247,225,0)"), []);
  const glitterTex = useMemo(() => makeRadialGlow("rgba(255,255,255,0.6)", "rgba(255,255,255,0)"), []);

  return (
    <group>
      {/* graded sky */}
      <mesh position={[0, 7, -9]}>
        <planeGeometry args={[80, 26]} />
        <meshBasicMaterial map={skyTex} fog={false} toneMapped={false} />
      </mesh>
      {/* sun glow high-right */}
      <mesh position={[7, 9.5, -8.95]} scale={[7, 7, 1]}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial
          map={sunTex}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          fog={false}
          toneMapped={false}
        />
      </mesh>
      {/* sea (reflective: catches IBL highlights via standard material) */}
      <mesh position={[0, 0.95, -8.7]}>
        <planeGeometry args={[80, 3.4]} />
        <meshStandardMaterial
          map={seaTex}
          roughness={0.16}
          metalness={0.25}
          envMapIntensity={1.5}
          fog={false}
        />
      </mesh>
      {/* bright horizon haze line where sky meets sea */}
      <mesh position={[0, 2.62, -8.62]}>
        <planeGeometry args={[80, 0.16]} />
        <meshBasicMaterial color="#eaf7fb" transparent opacity={0.7} fog={false} toneMapped={false} />
      </mesh>
      {/* sun glitter streak on the water under the sun */}
      <mesh position={[7, 0.8, -8.55]} scale={[2.6, 1.1, 1]}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial
          map={glitterTex}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          fog={false}
          toneMapped={false}
        />
      </mesh>
      {/* sandy beach strip */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, -7.4]}>
        <planeGeometry args={[80, 3.2]} />
        <meshStandardMaterial color="#ecd9b0" roughness={0.95} fog={false} />
      </mesh>
      {/* foam / surf line where the sea meets the sand */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.012, -8.45]}>
        <planeGeometry args={[80, 1.1]} />
        <meshBasicMaterial map={foamTex} transparent depthWrite={false} fog={false} toneMapped={false} />
      </mesh>
      {/* a couple of distant palm silhouettes */}
      <DistantPalm position={[-5.5, 0, -7.6]} />
      <DistantPalm position={[6.2, 0, -7.8]} scale={1.2} />
    </group>
  );
}

/** One arching palm frond built in the local XY plane (extends along +x, droops
 *  down via progressively steeper segments) with serrated leaflets along the
 *  rachis. The parent rotates it about Y to fan a crown of fronds around a trunk.
 *  `basic` uses unlit material (for the far backdrop silhouettes). */
function PalmFrond({
  length = 1.0,
  width = 0.16,
  lift = 0.45,
  color = "#3f8a52",
  basic = false,
}: {
  length?: number;
  width?: number;
  lift?: number;
  color?: string;
  basic?: boolean;
}) {
  const segs = 5;
  const segLen = length / segs;
  const ribs: JSX.Element[] = [];
  const leaflets: JSX.Element[] = [];
  let px = 0;
  let py = 0;
  let ang = -lift; // start angled up out of the crown; negative = upward
  for (let i = 0; i < segs; i++) {
    ang += (lift + 0.55) / segs + 0.12; // progressively droop downward
    const nx = px + Math.cos(ang) * segLen;
    const ny = py - Math.sin(ang) * segLen;
    const mx = (px + nx) / 2;
    const my = (py + ny) / 2;
    const rot = Math.atan2(ny - py, nx - px);
    const w = width * (1 - i / segs * 0.65);
    const matRib = basic ? (
      <meshBasicMaterial color={color} />
    ) : (
      <meshStandardMaterial color={color} roughness={1} flatShading side={THREE.DoubleSide} />
    );
    // central rachis blade
    ribs.push(
      <mesh key={`r${i}`} position={[mx, my, 0]} rotation={[Math.PI / 2, 0, rot]} castShadow={!basic}>
        <planeGeometry args={[segLen * 1.04, w]} />
        {matRib}
      </mesh>
    );
    // a pair of angled leaflets fanning off the midpoint
    const leafLen = w * 1.5;
    for (const s of [-1, 1]) {
      const matLeaf = basic ? (
        <meshBasicMaterial color={color} />
      ) : (
        <meshStandardMaterial
          color={color}
          roughness={1}
          flatShading
          side={THREE.DoubleSide}
        />
      );
      leaflets.push(
        <mesh
          key={`l${i}${s}`}
          position={[mx, my, s * w * 0.35]}
          rotation={[Math.PI / 2, s * 0.5, rot]}
          castShadow={!basic}
        >
          <planeGeometry args={[leafLen, w * 0.5]} />
          {matLeaf}
        </mesh>
      );
    }
    px = nx;
    py = ny;
  }
  return (
    <group>
      {ribs}
      {leaflets}
    </group>
  );
}

function DistantPalm({
  position,
  scale = 1,
}: {
  position: [number, number, number];
  scale?: number;
}) {
  const fronds = 9;
  return (
    <group position={position} scale={scale}>
      {/* slightly leaning trunk */}
      <group rotation={[0, 0, 0.06]}>
        <mesh position={[0, 1.4, 0]}>
          <cylinderGeometry args={[0.07, 0.12, 2.8, 8]} />
          <meshBasicMaterial color="#8a774e" />
        </mesh>
      </group>
      <group position={[0.17, 2.8, 0]}>
        {Array.from({ length: fronds }).map((_, i) => (
          <group key={i} rotation={[0, (i / fronds) * Math.PI * 2, 0]}>
            <PalmFrond length={1.3} width={0.2} lift={0.55} color="#3c8350" basic />
          </group>
        ))}
        {/* coconut cluster */}
        {[-0.08, 0.08, 0].map((x, i) => (
          <mesh key={i} position={[x, -0.05, (i - 1) * 0.07]}>
            <sphereGeometry args={[0.07, 8, 8]} />
            <meshBasicMaterial color="#6b5836" />
          </mesh>
        ))}
      </group>
    </group>
  );
}

/** Open balcony behind the desk. A proper Mediterranean hotel balustrade: a low
 *  stucco kneewall, white turned balusters between a moulded top + bottom rail,
 *  chunky newel posts at the ends, and a faint glass infill — so it reads as a
 *  real railing instead of one floating handrail line. */
function BalconyWall() {
  const white = "#f1ebe1";
  const rail = "#f6f1e7";
  const W = 6.4; // clear opening width
  const balusters = 22;
  return (
    <group position={[0, 0, -2.3]}>
      {/* header / soffit */}
      <mesh position={[0, 3.9, 0]}>
        <boxGeometry args={[10, 0.6, 0.25]} />
        <meshStandardMaterial color={white} roughness={0.95} />
      </mesh>
      {/* side jambs */}
      <mesh position={[-3.4, 2.2, 0]}>
        <boxGeometry args={[0.4, 4.2, 0.25]} />
        <meshStandardMaterial color={white} roughness={0.95} />
      </mesh>
      <mesh position={[3.4, 2.2, 0]}>
        <boxGeometry args={[0.4, 4.2, 0.25]} />
        <meshStandardMaterial color={white} roughness={0.95} />
      </mesh>

      {/* low stucco kneewall the balustrade sits on */}
      <mesh position={[0, 0.16, 0]} castShadow receiveShadow>
        <boxGeometry args={[W + 0.2, 0.32, 0.26]} />
        <meshStandardMaterial color="#e9e0d0" roughness={0.95} />
      </mesh>
      {/* bottom rail */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <boxGeometry args={[W, 0.07, 0.14]} />
        <meshStandardMaterial color={rail} roughness={0.6} metalness={0.15} />
      </mesh>
      {/* faint glass infill between the rails */}
      <mesh position={[0, 0.72, 0]}>
        <boxGeometry args={[W - 0.1, 0.6, 0.02]} />
        <meshStandardMaterial color="#cfeaf1" roughness={0.08} transparent opacity={0.16} />
      </mesh>
      {/* turned balusters */}
      {Array.from({ length: balusters }).map((_, i) => {
        const x = -W / 2 + 0.18 + (i * (W - 0.36)) / (balusters - 1);
        return (
          <group key={i} position={[x, 0.72, 0]}>
            <mesh castShadow>
              <cylinderGeometry args={[0.028, 0.028, 0.62, 10]} />
              <meshStandardMaterial color={rail} roughness={0.6} metalness={0.15} />
            </mesh>
            {/* little bulb mid-baluster for a turned profile */}
            <mesh position={[0, 0, 0]}>
              <sphereGeometry args={[0.045, 10, 8]} />
              <meshStandardMaterial color={rail} roughness={0.6} metalness={0.15} />
            </mesh>
          </group>
        );
      })}
      {/* chunky newel posts at the ends */}
      {[-W / 2 - 0.02, W / 2 + 0.02].map((x, i) => (
        <mesh key={i} position={[x, 0.62, 0]} castShadow>
          <boxGeometry args={[0.13, 1.06, 0.22]} />
          <meshStandardMaterial color="#eee7d8" roughness={0.85} />
        </mesh>
      ))}
      {/* moulded top handrail (caps the posts) */}
      <mesh position={[0, 1.08, 0]} castShadow>
        <boxGeometry args={[W + 0.34, 0.1, 0.22]} />
        <meshStandardMaterial color={rail} roughness={0.55} metalness={0.2} />
      </mesh>
      {/* slim under-rail shadow line for depth */}
      <mesh position={[0, 1.01, 0]}>
        <boxGeometry args={[W + 0.3, 0.02, 0.18]} />
        <meshStandardMaterial color="#d8cfbb" roughness={0.8} />
      </mesh>

      <BalconyCurtain />
    </group>
  );
}

/** A sheer curtain billowing at the right edge of the balcony opening. A few
 *  translucent vertical folds + a rod + tieband — soft framing for the view. */
function BalconyCurtain() {
  const folds = 6;
  return (
    <group position={[2.95, 0, 0.06]}>
      {/* rod */}
      <mesh position={[0, 3.5, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 1.0, 10]} />
        <meshStandardMaterial color="#b9a883" metalness={0.4} roughness={0.5} />
      </mesh>
      {Array.from({ length: folds }).map((_, i) => {
        const z = -0.4 + i * 0.16;
        const sway = Math.sin(i * 1.3) * 0.04;
        return (
          <mesh key={i} position={[sway, 1.9, z]} rotation={[0, 0, sway * 1.5]}>
            <boxGeometry args={[0.05, 3.0, 0.1]} />
            <meshStandardMaterial color="#fbf6ec" roughness={0.95} transparent opacity={0.55} />
          </mesh>
        );
      })}
    </group>
  );
}

/** Light-wood writing desk centred at the origin (laptop sits on it). */
function Desk() {
  const wood = "#caa776";
  const woodDark = "#a8895d";
  return (
    <group>
      <mesh position={[0, 0.74, -0.2]} castShadow receiveShadow>
        <boxGeometry args={[2.4, 0.05, 1.0]} />
        <meshStandardMaterial color={wood} roughness={0.6} metalness={0.05} />
      </mesh>
      {[
        [-1.1, 0.37, 0.2],
        [1.1, 0.37, 0.2],
        [-1.1, 0.37, -0.6],
        [1.1, 0.37, -0.6],
      ].map((p, i) => (
        <mesh key={i} position={p as [number, number, number]} castShadow>
          <boxGeometry args={[0.07, 0.74, 0.07]} />
          <meshStandardMaterial color={woodDark} roughness={0.6} />
        </mesh>
      ))}
      {/* notebook + pen */}
      <mesh position={[-0.55, 0.775, 0.12]} rotation={[0, 0.3, 0]} castShadow>
        <boxGeometry args={[0.26, 0.02, 0.34]} />
        <meshStandardMaterial color="#f7f3ea" roughness={0.9} />
      </mesh>
      <mesh position={[-0.5, 0.79, 0.14]} rotation={[0, 0.3, 0]} castShadow>
        <cylinderGeometry args={[0.008, 0.008, 0.16, 8]} />
        <meshStandardMaterial color="#2b6f7e" />
      </mesh>
      {/* coffee */}
      <group position={[0.7, 0.8, 0.18]}>
        <mesh castShadow>
          <cylinderGeometry args={[0.05, 0.045, 0.09, 16]} />
          <meshStandardMaterial color="#ffffff" roughness={0.5} />
        </mesh>
      </group>
    </group>
  );
}

/** Open laptop centred on the desk (the desktop device's physical stand-in). */
function Laptop() {
  return (
    <group position={[0, 0.77, -0.2]}>
      {/* base / keyboard deck */}
      <mesh position={[0, 0.012, 0.12]} castShadow>
        <boxGeometry args={[0.62, 0.024, 0.42]} />
        <meshStandardMaterial color="#cdd2d6" metalness={0.6} roughness={0.35} />
      </mesh>
      {/* keyboard inset — must lie FLAT on the deck (a plane defaults to vertical,
          which made it stand up as a dark "wall" in front of the screen). Raised
          well clear of the deck top (local ~0.024) + polygonOffset so the two
          near-coplanar surfaces don't z-fight and flicker as the camera zooms. */}
      <mesh position={[0, 0.034, 0.14]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[0.52, 0.3]} />
        <meshStandardMaterial
          color="#20262b"
          roughness={0.7}
          polygonOffset
          polygonOffsetFactor={-2}
          polygonOffsetUnits={-2}
        />
      </mesh>
      {/* trackpad (sits just above the keyboard inset) */}
      <mesh position={[0, 0.036, 0.3]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[0.18, 0.1]} />
        <meshStandardMaterial
          color="#aeb4b9"
          roughness={0.5}
          polygonOffset
          polygonOffsetFactor={-3}
          polygonOffsetUnits={-3}
        />
      </mesh>
      {/* lid + screen. Near-vertical (small recline) so the perpendicular desk
          camera sees it head-on; the screen is OFF (dark) — the DOM desktop blends
          on top once we've zoomed in, reading as the monitor powering on. */}
      <group position={[0, 0.2, -0.08]} rotation={[-0.06, 0, 0]}>
        <mesh castShadow>
          <boxGeometry args={[0.64, 0.42, 0.02]} />
          <meshStandardMaterial color="#b9bec3" metalness={0.6} roughness={0.35} />
        </mesh>
        <mesh position={[0, 0, 0.012]}>
          <planeGeometry args={[0.58, 0.36]} />
          <meshStandardMaterial color="#0a0d11" roughness={0.5} metalness={0.2} />
        </mesh>
      </group>
    </group>
  );
}

/** A rattan-style lounge chair in front of the desk (faces the laptop). */
function LoungeChair() {
  const frame = "#b48a5a";
  const cushion = "#e7ded0";
  return (
    <group position={[0, 0, 0.75]}>
      <mesh position={[0, 0.42, 0]} castShadow>
        <boxGeometry args={[0.6, 0.14, 0.56]} />
        <meshStandardMaterial color={cushion} roughness={0.95} />
      </mesh>
      <mesh position={[0, 0.78, 0.26]} rotation={[-0.18, 0, 0]} castShadow>
        <boxGeometry args={[0.6, 0.6, 0.12]} />
        <meshStandardMaterial color={cushion} roughness={0.95} />
      </mesh>
      {[-0.32, 0.32].map((x, i) => (
        <mesh key={i} position={[x, 0.55, 0]} castShadow>
          <boxGeometry args={[0.08, 0.2, 0.56]} />
          <meshStandardMaterial color={frame} roughness={0.7} />
        </mesh>
      ))}
      {[
        [-0.28, 0.18, 0.24],
        [0.28, 0.18, 0.24],
        [-0.28, 0.18, -0.24],
        [0.28, 0.18, -0.24],
      ].map((p, i) => (
        <mesh key={i} position={p as [number, number, number]} castShadow>
          <boxGeometry args={[0.06, 0.36, 0.06]} />
          <meshStandardMaterial color={frame} roughness={0.7} />
        </mesh>
      ))}
    </group>
  );
}

/** Potted palm for the suite. */
function PalmPlant({
  position,
  scale = 1,
}: {
  position: [number, number, number];
  scale?: number;
}) {
  const fronds = 10;
  const greens = ["#3f8a52", "#46955a", "#367a49", "#4ea063"];
  return (
    <group position={position} scale={scale}>
      {/* pot */}
      <mesh position={[0, 0.24, 0]} castShadow>
        <cylinderGeometry args={[0.2, 0.15, 0.48, 16]} />
        <meshStandardMaterial color="#d8d2c6" roughness={0.9} />
      </mesh>
      {/* soil */}
      <mesh position={[0, 0.47, 0]}>
        <cylinderGeometry args={[0.19, 0.19, 0.04, 16]} />
        <meshStandardMaterial color="#3a2d1f" roughness={1} />
      </mesh>
      {/* trunk (slight lean + taper) */}
      <group rotation={[0.05, 0, 0.04]}>
        <mesh position={[0, 0.78, 0]} castShadow>
          <cylinderGeometry args={[0.045, 0.075, 1.05, 10]} />
          <meshStandardMaterial color="#8a7350" roughness={0.95} />
        </mesh>
      </group>
      {/* crown of arching fronds */}
      <group position={[0.04, 1.3, 0]}>
        {Array.from({ length: fronds }).map((_, i) => (
          <group
            key={i}
            rotation={[0, (i / fronds) * Math.PI * 2, 0]}
          >
            <PalmFrond
              length={0.78 + (i % 3) * 0.08}
              width={0.16}
              lift={0.5}
              color={greens[i % greens.length]}
            />
          </group>
        ))}
        {/* a few short upright spear fronds at the very centre */}
        {[0, 1, 2].map((i) => (
          <group key={`c${i}`} rotation={[0, i * 2.1, 0]}>
            <PalmFrond length={0.42} width={0.1} lift={1.0} color="#54a86a" />
          </group>
        ))}
      </group>
    </group>
  );
}

/** A hint of a hotel bed against the left side, mostly out of the main frame. */
function Bed() {
  return (
    <group position={[-3.4, 0, 1.0]} rotation={[0, Math.PI / 2, 0]}>
      {/* base */}
      <mesh position={[0, 0.28, 0]} castShadow receiveShadow>
        <boxGeometry args={[2.0, 0.4, 1.5]} />
        <meshStandardMaterial color="#cdbf9f" roughness={0.9} />
      </mesh>
      {/* mattress + duvet */}
      <mesh position={[0, 0.56, 0]} castShadow>
        <boxGeometry args={[1.94, 0.2, 1.44]} />
        <meshStandardMaterial color="#f7f4ee" roughness={0.95} />
      </mesh>
      {/* pillows */}
      {[-0.4, 0.4].map((z, i) => (
        <mesh key={i} position={[-0.7, 0.7, z]} castShadow>
          <boxGeometry args={[0.5, 0.14, 0.5]} />
          <meshStandardMaterial color="#ffffff" roughness={0.95} />
        </mesh>
      ))}
      {/* folded throw blanket at the foot of the bed (soft sand, draped) */}
      <mesh position={[0.62, 0.7, 0]} castShadow>
        <boxGeometry args={[0.5, 0.12, 1.46]} />
        <meshStandardMaterial color="#dccdb4" roughness={0.95} />
      </mesh>
    </group>
  );
}
